import { channelLabel, flagOf } from './analyticsFormat';

/**
 * Which cards open out, and into what.
 *
 * The dashboard is a set of ANSWERS — the eight referrers that matter, the shape
 * of the week, the last dozen visits. The question after most of them is "and
 * then what", and until now the only way to ask it was the CSV export: every
 * ranked row already carries pageviews, bounce rate, engaged time and attributed
 * revenue, and the card drew three of those columns because a third of a
 * dashboard has room for three.
 *
 * So a card's expanded view is deliberately not the same card, bigger. It is the
 * same question asked at the depth the data already answers: every row rather
 * than the top eight, every column rather than the three that fit, and — where
 * the dimension has siblings the card never had room to name — those too. The
 * campaign card gains sources and mediums, the pages card gains the pages visits
 * ENDED on, the devices card gains operating systems.
 *
 * A card that already shows everything it has is deliberately ABSENT from this
 * map: the funnel's steps and the three booking figures beside them are the
 * whole answer, so an expand control over them would promise a second screen
 * with nothing new on it. `canExpand` is the one place that decides, so "not
 * applicable" is a fact stated once rather than a rule the card chrome has to
 * remember.
 *
 * What a TABBED card tabulates is not decided here, though, only defaulted:
 * `acquisition`, `content` and `devices` each carry three dimensions and open
 * out on whichever one the reader has in front of them, so the screen passes
 * `tables` and these entries are the fallback.
 */
export interface DetailTable {
  /** A `dim` the /analytics/top endpoint accepts — see DIMENSIONS server-side. */
  dim: string;
  /** Narrows `region` and `city` to one country; ignored on every other dim. */
  country?: string;
  /** Rewrites the stored value for display: a channel slug, a country flag. */
  label?: (value: string) => string;
}

export const DETAIL_TABLES: Record<string, DetailTable[]> = {
  acquisition: [{ dim: 'channel', label: channelLabel }],
  content: [{ dim: 'page' }, { dim: 'exit_page' }],
  devices: [{ dim: 'device' }, { dim: 'browser' }, { dim: 'os' }],
  geo: [{ dim: 'country', label: (value) => `${flagOf(value)} ${value}`.trim() }],
  // Nothing tabular to add: what these open out into is the drawing the card
  // already holds, at the size a third of a grid could not give it. The screen
  // hands that in; this map only has to say that they open at all.
  trend: [],
  hours: [],
  live: [],
  // Its own body — paging and two filters are state, so they live with the
  // table rather than being configured from here.
  sessions: [],
};

export const canExpand = (id: string): boolean => id in DETAIL_TABLES;

/** The server caps `top` at 100 (the `/analytics/top` handler). */
export const DETAIL_LIMIT = 100;

/**
 * Mirrors PAGE_SIZE in controller/analyticsCont.js, and only as a fallback: the
 * response carries its own, so a fixture that pages differently is honoured.
 */
export const FALLBACK_PAGE_SIZE = 50;
